import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Haskell and randomness",
  "description": "",
  "published": "2012-07-02T00:00:00.000Z",
  "redirect_from": ["/blog/haskell-and-randomness/swizec/4763"],
  "categories": "Algorithm, Haskell, Programming, Pseudorandom number generator, Pseudorandom Numbers, Random number generation, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`I finally started writing code for my `}<a parentName="p" {...{
        "href": "http://swizec.github.com/Le-Thesis/"
      }}>{`thesis`}</a>{`. Hooray!`}</p>
    <p><img parentName="p" {...{
        "src": "https://upload.wikimedia.org/wikipedia/en/thumb/8/89/Haskell_wiki.JPG/300px-Haskell_wiki.JPG",
        "alt": "A row of businesses in downtown Haskell.",
        "title": "A row of businesses in downtown Haskell."
      }}></img></p>
    <p>{`To get my feet wet I decided to write a basic framework for `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Evolutionary_algorithm",
        "title": "Evolutionary algorithm"
      }}>{`evolutionary algorithms`}</a>{` in `}<a parentName="p" {...{
        "href": "http://haskell.org",
        "title": "Haskell (programming language)"
      }}>{`Haskell`}</a>{`. Nothing too major, just a way to easily assemble different evaluation, mutation and selection functions.`}</p>
    <p>{`For starters I'm going to evolve a `}<em parentName="p">{`"`}<a parentName="em" {...{
          "href": "http://en.wikipedia.org/wiki/Hello_world_program",
          "title": "Hello world program"
        }}>{`Hello World!`}</a>{`"`}</em>{` string. How hard could it be? Took me `}<a parentName="p" {...{
        "href": "http://swizec.com/blog/evolving-a-poem-with-an-hour-of-python-hacking/swizec/1505"
      }}>{`an hour to evolve a poem in python`}</a>{` based on character mutations ...`}</p>
    <p>{`Turns out it's a lot more difficult to do in Haskell.`}</p>
    <p>{`Trouble starts as soon as you try generating a random population, because getting random numbers in Haskell is somewhat involved. Or rather, it looks scarily involved at first.`}</p>
    <h2 {...{
      "id": "random-generators"
    }}>{`Random Generators`}</h2>
    <p>{`The main problem comes from how `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Pseudorandom_number_generator",
        "title": "Pseudorandom number generator"
      }}>{`pseudo-random`}</a>{` generators work. Essentially you start with a `}<em parentName="p">{`seed`}</em>{` - it can be anything, I hear current time is a good starting seed - then every time somebody needs a new random number, you perform some formula on the `}<em parentName="p">{`seed`}</em>{` and get a number.`}</p>
    <p>{`The next number then depends on the previous number and so on until infinity. Your garden variety recursively defined series. Not very random at all, but works well enough for most.`}</p>
    <p>{`Noticed the problem?`}</p>
    <p>{`Yep, random generators rely on state and state is this hated, somewhat annoying thing to handle in Haskell. Monads may sound simple, but until you get the hang of them everything looks a bit odd.`}</p>
    <p>{`Oh and you wouldn't want to pollute your whole codebase with a monad just because you want to start your whole algorithm with some random stuff, would you?`}</p>
    <p>{`No you wouldn't, it's messy.`}</p>
    <p>{`After a lot of searching (and a lot of scary suggestions), this was the cleanest solution I could find:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`import System.Random

-- takes a random generator and returns a list of strings of 50 chars
start_population :: (RandomGen g) => g -> [[Char]]
start_population gen =
  [take 50 $ randomRs ('A', 'z') gen | x <- [0..]]

main = do
  randomGen <- newStdGen  -- get a random generator

  print $ take 2 $ start_population randomGen  -- use it as a function argument
`}</code></pre>
    <p>{`The best part about this approach is that Haskell automatically gives you a `}<em parentName="p">{`standard generator,`}</em>{` which already takes a seed from some sort of input - not sure what it uses - so your results will look reasonably random.`}</p>
    <p>{`But when you want to do testing, you can just as easily do this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-haskell"
      }}>{`let rand = mkStdGen 42
print $ take 2 $ randomRs ('a', 'z') rand
`}</code></pre>
    <p>{`Which will `}<em parentName="p">{`always`}</em>{` print the same two characters `}<em parentName="p">{`"nd"`}</em>{`.`}</p>
    <p>{`Very useful when you want to test your code actually works!`}</p>
    <p>{`There's just one problem with this approach - it's very difficult to up and decide that hey, this particular function right here, should be somewhat random from now on! You now have to tell the whole system and everyone using the function, that you'd like a random generator please.`}</p>
    <p>{`Whether you should want to do that without telling anyone ... well, that's a whole other story.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.r-bloggers.com/quasi-random-number-generation-in-r/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_05_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.r-bloggers.com/quasi-random-number-generation-in-r/"
        }}>{`Quasi-Random Number Generation in R`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://cs.stackexchange.com/questions/2576/most-efficient-algorithm-to-print-1-100-using-a-given-random-number-generator"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_10_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://cs.stackexchange.com/questions/2576/most-efficient-algorithm-to-print-1-100-using-a-given-random-number-generator"
        }}>{`Most efficient algorithm to print 1-100 using a given random number generator`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/why-i-still-havent-learned-haskell/swizec/4663"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/94978044_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://swizec.com/blog/why-i-still-havent-learned-haskell/swizec/4663"
        }}>{`Why I still haven't learned Haskell`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.haskell.org/haskellwiki/index.php?title=Future_of_Haskell&diff=46219&oldid=prev"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_16_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.haskell.org/haskellwiki/index.php?title=Future_of_Haskell&diff=46219&oldid=prev"
        }}>{`Future of Haskell`}</a></li>
    </ul>
    <hr></hr>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.reddit.com/r/haskell/comments/vi425/what_are_everyones_favorite_aha_moments_in/"
        }}><img parentName="a" {...{
            "src": "https://i.zemanta.com/noimg_20_80_80.jpg",
            "alt": null
          }}></img></a><a parentName="li" {...{
          "href": "http://www.reddit.com/r/haskell/comments/vi425/what_are_everyones_favorite_aha_moments_in/"
        }}>{`What are everyone's favorite "aha" moments in learning Haskell?`}</a></li>
    </ul>
    <hr></hr>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/?px",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=59e72f89-c756-45f4-96c1-5afa691394af",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      